/* eslint-disable no-shadow */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import { DragIcon, ProductIcon, SettingIcon } from '../../../../utils/SVGIcon';
import { GetDate } from '../../../../utils/Show';
import { mkRelativeUrl } from '../../../../utils/URL';
import cn from '../../../../utils/cn';
import { handleSetProductSequence } from '../ProductOperation';
import { useProductSetting } from '../ProductSettingProvider';

function ProductTr({ item, provided, getItemStyle, snapshot, drag = true }) {
    const navigate = useNavigate();
    return (
        <tr
            ref={drag ? provided?.innerRef : null}
            {...provided?.draggableProps}
            {...provided?.dragHandleProps}
            style={drag ? getItemStyle(snapshot?.isDragging, provided?.draggableProps?.style) : {}}
            className={cn(
                `group border-b border-primary-100 hover:bg-secondary-50 h-12.5 [&>*:first-of-type]:!pl-5 `,
                {
                    'placeholder-mode': item?.demo
                }
            )}
        >
            <td>
                <span className="inline-flex items-center justify-start gap-2.5">
                    {drag && (
                        <button type="button" className={cn('inline-block w-4 h-4')}>
                            <DragIcon />
                        </button>
                    )}
                    <span>{item.id}</span>
                </span>
            </td>
            <td>
                <span>
                    {item?.logoUrl ? (
                        <img
                            src={item?.logoUrl}
                            alt={item?.applicationTitle}
                            className="inline-block size-[35px] rounded-[7px] "
                        />
                    ) : (
                        <ProductIcon className="size-[35px] rounded-[7px] " />
                    )}
                </span>
            </td>
            <td
                className="group-hover:text-secondary group-hover:font-medium hover:underline"
                onClick={() => navigate(mkRelativeUrl(`/settings/products/${item.id}/info`))}
            >
                <span>{item?.applicationTitle}</span>
            </td>

            <td>
                <span>{item.description}</span>
            </td>
            <td className="text-left">
                <span>{GetDate(item.timeCreated)}</span>
            </td>
            <td className="text-left">
                <span>{GetDate(item.timeUpdated)}</span>
            </td>
            <td className="text-center">
                <span
                    className={cn('capitalize text-success', {
                        'text-warning': item.status !== 'active'
                    })}
                >
                    {item.status}
                </span>
            </td>
            <td>
                <span className="inline-block text-right w-full pr-3">
                    <button
                        type="button"
                        onClick={() =>
                            navigate(mkRelativeUrl(`/settings/products/${item.id}/info`))
                        }
                        className="bg-[#FDF1E0] text-warning w-[31px] p-[7px] rounded-[7px]"
                    >
                        <SettingIcon />
                    </button>
                </span>
            </td>
        </tr>
    );
}
function ProductTableHead() {
    return (
        <thead>
            <tr>
                <th scope="col">
                    <span>ID</span>
                </th>

                <th scope="col" className="w-20 text-left">
                    <span>Logo</span>
                </th>
                <th scope="col" className="w-40">
                    <span>Product Name</span>
                </th>
                <th scope="col">
                    <span>Short Description</span>
                </th>
                <th scope="col" className="w-32 !px-1 text-left">
                    <span>Created Date</span>
                </th>
                <th scope="col" className="w-32 !px-1 text-left">
                    <span>Last Update</span>
                </th>
                <th scope="col" className="w-20 text-center">
                    <span>Status</span>
                </th>
                <th scope="col">#</th>
            </tr>
        </thead>
    );
}
function ProductListBody({ data }) {
    const { products, setProducts, handleReload, productFilter } = useProductSetting();
    const { setMessage } = useTemplate();

    const searchValue = productFilter?.searchValue || '';
    const status = productFilter?.status || '';

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: 'none',
        background: isDragging ? '#E6F4F1' : '#fff',
        zIndex: isDragging ? 999999 : 111,
        ...draggableStyle
    });
    const getListStyle = () => ({
        width: '100%'
    });

    const handleOnDragEnd = (result) => {
        if (!result.destination || searchValue?.length) return; // Stop drag if searchValue has a length
        const items = Array.from(products);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result?.destination?.index, 0, reorderedItem);
        setProducts(items);
        const formData = {
            applicationIds: items?.map((item) => item?.id)
        };
        if (result.source.index !== result.destination.index) {
            handleSetProductSequence(formData)
                .then(({ title }) => {
                    setMessage({ type: 'success', text: title });
                    handleReload();
                })
                .catch((err) => {
                    setMessage({ type: 'error', text: err.toString(0) });
                });
        }
    };
    return (
        <div className="overflow-hidden">
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="products">
                    {(provided) => (
                        <table
                            ref={provided.innerRef}
                            style={getListStyle()}
                            className="bik-table mb-40 table-fixed"
                            cellPadding={0}
                            cellSpacing={0}
                            {...provided.droppableProps}
                        >
                            <ProductTableHead />
                            <tbody>
                                {data?.length === 0 && (
                                    <tr className="!bg-inherit">
                                        <td colSpan={8} className="!text-left">
                                            <span className="text-primary-700 ">
                                                No product Found!
                                            </span>
                                        </td>
                                    </tr>
                                )}
                                {data?.map((item, index) => {
                                    if (searchValue?.length || status?.length) {
                                        return <ProductTr key={item.id} drag={false} item={item} />;
                                    }
                                    return (
                                        <Draggable
                                            key={item.id}
                                            draggableId={item.id.toString()}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <ProductTr
                                                    provided={provided}
                                                    item={item}
                                                    index={index}
                                                    snapshot={snapshot}
                                                    getItemStyle={getItemStyle}
                                                />
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </tbody>
                        </table>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}

export default ProductListBody;
